import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { queryKeys } from '@/config';
import { authApi } from '@/features/auth/api';
import { useAuthStore } from '@/stores';
import type { PermissionKey } from '@/types';

export function useAuth() {
  const {
    user,
    token,
    isAuthenticated,
    isLoading,
    setAuth,
    updateFromProfile,
    logout: storeLogout,
    hydrate,
  } = useAuthStore();
  const queryClient = useQueryClient();

  // Hydrate on mount
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Fetch profile when authenticated
  const { data: profile } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: authApi.me,
    enabled: isAuthenticated && !!token,
    staleTime: 5 * 60 * 1000,
  });

  // Update user from profile data
  useEffect(() => {
    if (profile) {
      updateFromProfile(profile);
    }
  }, [profile, updateFromProfile]);

  // The socket is owned by <WebSocketLifecycle /> at the app root, not here:
  // useAuth() runs in every page, so tearing the singleton down on unmount
  // killed the shared connection on every route change.

  const login = async (email: string, password: string) => {
    // Očisti keš prethodnog korisnika pre novog login-a
    queryClient.removeQueries({ queryKey: queryKeys.auth.me });

    const response = await authApi.login({ email, password });
    const { user: u } = response;
    setAuth(response.accessToken, {
      id: u.id,
      tenantId: u.tenantId,
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      phone: null,
      systemRole: u.systemRole,
      emailNotifications: true,
      locale: 'sr',
      isActive: true,
      canAccessDashboard: u.canAccessDashboard,
      canEditShows: u.canEditShows,
      canEditWorkflows: u.canEditWorkflows,
      canEditEquipment: u.canEditEquipment,
      canEditTeam: u.canEditTeam,
      canEditRoles: u.canEditRoles,
      canEditPlanner: u.canEditPlanner,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      roles: u.roles,
    });
    return response;
  };

  const logout = useCallback(() => {
    storeLogout();
    queryClient.clear();
  }, [storeLogout, queryClient]);

  const isAdmin = user?.systemRole === 'SYSTEM_ADMIN' || user?.systemRole === 'TENANT_ADMIN';
  const canAccessDashboard = isAdmin || user?.canAccessDashboard === true;

  const canEdit = useCallback(
    (permission: PermissionKey): boolean => {
      if (isAdmin) return true;
      return user?.[permission] === true;
    },
    [isAdmin, user]
  );

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    isAdmin,
    canAccessDashboard,
    canEdit,
    login,
    logout,
  };
}
